import { useState, useEffect } from 'react';
import { Radio, Plus, X, Check, Trash2, Upload } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const uploadFile = async (file, category) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API_URL}/upload?category=${category}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data.url;
};

const emptyEvent = {
  title: '', description: '', stream_url: '', platform: 'youtube',
  thumbnail_url: '', scheduled_at: '', is_premium: false
};

export default function AdminLiveEvents() {
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newEvent, setNewEvent] = useState(emptyEvent);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/events`);
      setEvents(res.data);
    } catch {
      toast.error('Failed to load live events');
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const isPast = (event) => {
    if (!event.scheduled_at) return false;
    return !event.is_live && new Date(event.scheduled_at).getTime() < Date.now();
  };

  const handleThumbnailUpload = async (e, isEditing = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setUploading(true);
    try {
      const url = await uploadFile(file, 'image');
      if (isEditing) setEditingEvent(prev => ({ ...prev, thumbnail_url: url }));
      else setNewEvent(prev => ({ ...prev, thumbnail_url: url }));
      toast.success('Thumbnail uploaded!');
    } catch { toast.error('Failed to upload thumbnail'); }
    finally { setUploading(false); }
  };

  const createEvent = async () => {
    if (!newEvent.title || !newEvent.stream_url || !newEvent.scheduled_at) {
      toast.error('Title, stream URL, and scheduled date/time are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/events`, newEvent);
      toast.success('Live event created!');
      setShowCreate(false);
      setNewEvent(emptyEvent);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const saveEvent = async (event) => {
    try {
      await axios.put(`${API_URL}/admin/events/${event.id}`, {
        title: event.title,
        description: event.description,
        stream_url: event.stream_url,
        platform: event.platform,
        thumbnail_url: event.thumbnail_url,
        scheduled_at: event.scheduled_at,
        is_premium: event.is_premium
      });
      toast.success('Event updated');
      setEditingEvent(null);
      fetchEvents();
    } catch { toast.error('Failed to update event'); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this live event? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/admin/events/${id}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch { toast.error('Failed to delete event'); }
  };

  const toggleLive = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/admin/events/${id}/go-live`);
      toast.success(res.data.is_live ? 'Event is now live!' : 'Event set back to scheduled');
      fetchEvents();
    } catch { toast.error('Failed to update live status'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Manage Live Events</h2>
        <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="add-live-event-button">
          <Plus className="w-4 h-4 mr-2" /> Add Event
        </button>
      </div>

      {showCreate && (
        <div className="glass-card p-6 mb-4">
          <h3 className="text-lg font-semibold mb-4">Add New Live Event</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input value={newEvent.title} onChange={(e) => setNewEvent({...newEvent, title: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Platform</Label>
              <select value={newEvent.platform} onChange={(e) => setNewEvent({...newEvent, platform: e.target.value})} className="input-dark mt-1 w-full rounded-md">
                <option value="youtube">YouTube</option>
                <option value="twitch">Twitch</option>
                <option value="instagram">Instagram</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Stream URL</Label>
              <Input value={newEvent.stream_url} onChange={(e) => setNewEvent({...newEvent, stream_url: e.target.value})} className="input-dark mt-1" placeholder="https://youtube.com/watch?v=..." />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Input value={newEvent.description} onChange={(e) => setNewEvent({...newEvent, description: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Scheduled Date/Time</Label>
              <Input type="datetime-local" value={newEvent.scheduled_at} onChange={(e) => setNewEvent({...newEvent, scheduled_at: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Thumbnail Image</Label>
              <div className="flex gap-2 mt-1">
                <Input value={newEvent.thumbnail_url} onChange={(e) => setNewEvent({...newEvent, thumbnail_url: e.target.value})} className="input-dark flex-1" placeholder="URL or upload" />
                <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload'}
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleThumbnailUpload(e, false)} disabled={uploading} />
                </label>
              </div>
              {newEvent.thumbnail_url && <img src={newEvent.thumbnail_url} alt="Thumbnail" className="w-20 h-12 rounded object-cover mt-2" />}
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input type="checkbox" id="new-event-premium" checked={newEvent.is_premium} onChange={(e) => setNewEvent({...newEvent, is_premium: e.target.checked})} />
              <Label htmlFor="new-event-premium">Premium exclusive</Label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button onClick={createEvent} className="btn-primary"><Plus className="w-4 h-4 mr-2" /> Add Event</button>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Radio className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Live Events Yet</h3>
          <p className="text-gray-400 mb-4">Add your first live event to get started</p>
        </div>
      ) : (
        events.map(event => (
          <div key={event.id} className="glass-card p-4" data-testid={`live-event-${event.id}`}>
            {editingEvent?.id === event.id ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Title</Label><Input value={editingEvent.title} onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})} className="input-dark mt-1" /></div>
                  <div>
                    <Label>Platform</Label>
                    <select value={editingEvent.platform} onChange={(e) => setEditingEvent({...editingEvent, platform: e.target.value})} className="input-dark mt-1 w-full rounded-md">
                      <option value="youtube">YouTube</option>
                      <option value="twitch">Twitch</option>
                      <option value="instagram">Instagram</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="md:col-span-2"><Label>Stream URL</Label><Input value={editingEvent.stream_url} onChange={(e) => setEditingEvent({...editingEvent, stream_url: e.target.value})} className="input-dark mt-1" /></div>
                  <div className="md:col-span-2"><Label>Description</Label><Input value={editingEvent.description} onChange={(e) => setEditingEvent({...editingEvent, description: e.target.value})} className="input-dark mt-1" /></div>
                  <div>
                    <Label>Scheduled Date/Time</Label>
                    <Input type="datetime-local" value={editingEvent.scheduled_at ? editingEvent.scheduled_at.slice(0, 16) : ''} onChange={(e) => setEditingEvent({...editingEvent, scheduled_at: e.target.value})} className="input-dark mt-1" />
                  </div>
                  <div>
                    <Label>Thumbnail</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={editingEvent.thumbnail_url} onChange={(e) => setEditingEvent({...editingEvent, thumbnail_url: e.target.value})} className="input-dark flex-1" />
                      <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                        <Upload className="w-4 h-4" />{uploading ? '...' : 'Upload'}
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleThumbnailUpload(e, true)} disabled={uploading} />
                      </label>
                    </div>
                    {editingEvent.thumbnail_url && <img src={editingEvent.thumbnail_url} alt="Thumbnail" className="w-20 h-12 rounded object-cover mt-1" />}
                  </div>
                  <div className="flex items-center gap-2 mt-6">
                    <input type="checkbox" checked={editingEvent.is_premium} onChange={(e) => setEditingEvent({...editingEvent, is_premium: e.target.checked})} />
                    <Label>Premium exclusive</Label>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingEvent(null)} className="btn-ghost"><X className="w-4 h-4" /></button>
                  <button onClick={() => saveEvent(editingEvent)} className="btn-primary"><Check className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <img src={event.thumbnail_url || '/placeholder-album.png'} alt={event.title} className="w-20 h-12 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{event.title}</h3>
                    {event.is_live && <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">LIVE</span>}
                    {isPast(event) && <span className="px-2 py-0.5 text-xs rounded-full bg-white/10 text-gray-400">Past</span>}
                    {event.is_premium && <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400">Premium</span>}
                  </div>
                  <p className="text-sm text-gray-400 truncate">{event.description}</p>
                  <p className="text-xs text-gray-500">
                    {event.scheduled_at ? new Date(event.scheduled_at).toLocaleString() : 'No date set'} · {event.platform}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => toggleLive(event.id)} className="btn-ghost" data-testid={`toggle-live-${event.id}`}>
                    {event.is_live ? 'End Live' : 'Go Live'}
                  </button>
                  <button onClick={() => setEditingEvent({...event})} className="btn-ghost">Edit</button>
                  <button onClick={() => deleteEvent(event.id)} className="btn-ghost text-red-400" data-testid={`delete-live-event-${event.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
